<template>
  <Transition name="fade">
    <div class="dialog-view" :class="{ 'is-confirm-style': dialogContent.dialogType != DialogType.notice }" v-if="dialogContent.content" @click="confirm(false)">
      <div class="dialog-card" @click.stop :key="dialogContent.content">
        <div class="dialog-inner">
          <div class="header-section" v-if="dialogContent.dialogType == DialogType.notice">
            <div class="status-icon" :class="dialogContent.isSuccess ? 'success' : 'error'">
              <svg viewBox="0 0 24 24" class="svg-element">
                <template v-if="dialogContent.isSuccess">
                  <path d="M20 6L9 17l-5-5" fill="none" stroke="currentColor" stroke-width="2.5" stroke-linecap="round" stroke-linejoin="round" />
                </template>
                <template v-else>
                  <path d="M18 6L6 18M6 6l12 12" fill="none" stroke="currentColor" stroke-width="2.5" stroke-linecap="round" stroke-linejoin="round" />
                </template>
              </svg>
            </div>
          </div>

          <div class="body-section">
            <div class="message-content">{{ dialogContent.content }}</div>
          </div>

          <div class="footer-section" v-if="dialogContent.dialogType != DialogType.notice">
            <div class="confirm-wrap" v-if="dialogContent.dialogType == DialogType.confirm">
              <div class="action-btn primary-action" @click="confirm(true)">
                {{ $t('confirm') }}
              </div>
              <div class="action-btn secondary-action" @click="confirm(false)">
                {{ $t('cancel') }}
              </div>
            </div>
            <div class="confirm-wrap" v-if="dialogContent.dialogType == DialogType.restart">
              <div class="action-btn primary-action" @click="confirm(true)">
                {{ $t('restart') }}
              </div>
            </div>
            <div class="confirm-wrap" v-if="dialogContent.dialogType == DialogType.alert">
              <div class="action-btn primary-action single-btn" @click="confirm(true)">
                {{ $t('ok') }}
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  </Transition>
</template>

<script setup lang="ts">
import { DialogType, dialogContent } from './dialog'

const confirm = (value: boolean) => {
  if (dialogContent.resolve != null) {
    dialogContent.resolve(value)
    dialogContent.content = ''
    dialogContent.dialogType = DialogType.notice
    dialogContent.resolve = null
    dialogContent.reject = null
  }
}
</script>

<style scoped lang="less">
.dialog-view {
  position: fixed;
  display: flex;
  justify-content: center;
  align-items: center;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  z-index: 2000;
  -webkit-app-region: no-drag;
  pointer-events: none;
  background: transparent;
  backdrop-filter: none;
  transition: var(--transition-base);
  background-color: yellow;

  &.is-confirm-style {
    background: var(--glass-overlay-bg);
    backdrop-filter: blur(var(--glass-overlay-blur));
    pointer-events: auto;
  }

  /* Notice 模式下也启用背景遮罩，但允许点击穿透 */
  &:not(.is-confirm-style) {
    background: var(--glass-overlay-bg);
    backdrop-filter: blur(var(--glass-overlay-blur));
    /* 让 notice 也能有背景毛玻璃，但由于 pointer-events: none 仍能点击后面 */
  }

  .dialog-card {
    position: relative;
    background: var(--color-white);
    border: 1px solid rgba(255, 255, 255, 0.1);
    border-radius: var(--radius-l);
    box-shadow: 0 10px 40px rgba(0, 0, 0, 0.08); /* 遵循 V2 投影规范 */
    min-width: 320px;
    max-width: 90%;
    overflow: hidden;
    pointer-events: auto;
  }

  .dialog-inner {
    padding: 32px 32px;
    display: flex;
    flex-direction: column;
    align-items: center;

    .header-section {
      margin-bottom: 20px;

      .status-icon {
        width: 56px;
        height: 56px;
        border-radius: 50%;
        display: flex;
        justify-content: center;
        align-items: center;
        background: rgba(0, 0, 0, 0.03);

        .svg-element {
          width: 28px;
          height: 28px;
        }

        &.success {
          background: rgba(var(--color-brand-1-rgb), 0.1);
          color: var(--brand-color-pure);
        }

        &.error {
          background: rgba(var(--color-brand-2-rgb), 0.1);
          color: var(--color-brand-2);
        }
      }
    }

    .body-section {
      .message-content {
        font-size: 16px;
        font-weight: 700; /* 标题加粗增强对比 */
        color: var(--color-dark);
        text-align: center;
        line-height: 1.5;
        white-space: pre-wrap;
      }
    }

    .footer-section {
      width: 100%;
      margin-top: 32px;

      .confirm-wrap {
        display: flex;
        gap: 16px;
        justify-content: center;

        .action-btn {
          flex: 1;
          min-width: 120px;
          height: 44px;
          display: flex;
          justify-content: center;
          align-items: center;
          cursor: pointer;
          border-radius: var(--action-btn-radius);
          font-size: 14px;
          font-weight: 700;
          transition: var(--transition-base);

          &.primary-action {
            background: var(--brand-color);
            color: #fff;
            box-shadow: var(--action-btn-primary-shadow);

            &:hover {
              transform: translateY(-2px);
              box-shadow: var(--action-btn-primary-shadow-hover);
            }
          }

          &.secondary-action {
            background: var(--color-gray-1);
            color: var(--color-dark);

            &:hover {
              background: #f0f0f0;
              transform: translateY(-2px);
            }
          }

          &.single-btn {
            width: 100%;
            height: 48px;
            font-size: 16px;
            letter-spacing: 1px;
            /* 适配截图中的大按钮风格 */
            box-shadow: 0 8px 20px rgba(var(--color-brand-1-rgb), 0.25);
          }

          &:active {
            transform: translateY(0);
          }
        }
      }
    }
  }
}

@keyframes dialogEnter {
  from {
    opacity: 0;
    transform: translateY(-20px) scale(0.95);
  }
  to {
    opacity: 1;
    transform: translateY(0) scale(1);
  }
}

.fade-enter-active,
.fade-leave-active {
  transition: var(--transition-base);
  .dialog-card {
    transition: var(--transition-base);
  }
}

.fade-enter-from,
.fade-leave-to {
  opacity: 0;
  .dialog-card {
    transform: translateY(-20px) scale(0.95);
  }
}
</style>
